'use client';

import { useCallback, useRef } from 'react';

import { useVisitorData } from '@fingerprint/react';

/**
 * Provides a lazy, cached getter for a Fingerprint requestId.
 * The fingerprint API is only called on the first invocation of
 * getRequestId(); subsequent calls return the cached value.
 */
export function useFingerprintRequestId() {
  const { getData } = useVisitorData({ immediate: false });
  const getDataRef = useRef(getData);
  getDataRef.current = getData;
  const cachedIdRef = useRef<string | null>(null);
  const inflightRef = useRef<Promise<string | null> | null>(null);

  const getRequestId = useCallback(async (): Promise<string | null> => {
    if (cachedIdRef.current) return cachedIdRef.current;
    if (inflightRef.current) return inflightRef.current;

    inflightRef.current = (async () => {
      try {
        let fpTimeout: ReturnType<typeof setTimeout>;
        const result = await Promise.race([
          getDataRef.current(),
          new Promise<null>(resolve => {
            fpTimeout = setTimeout(() => resolve(null), 2000);
          }),
        ]).catch(() => null);
        clearTimeout(fpTimeout!);
        const id = result?.event_id ?? null;
        if (id) cachedIdRef.current = id;
        return id;
      } catch {
        return null;
      } finally {
        inflightRef.current = null;
      }
    })();

    return inflightRef.current;
  }, []);

  return { getRequestId };
}
